import { observer } from 'mobx-react';
import { useContext } from 'react';
import { T, translation, TranslationsContext } from '../../../../Translations';
import SectionCard from './SectionCard';

interface Props {
  name: string;
  description: string;
  active: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onActiveChange: (active: boolean) => void;
  /** Section number in the parent page's section list */
  sectionNumber?: number;
}

/**
 * Group Details section — name, description, active-toggle. Wraps its fields
 * in the shared SectionCard so it matches the Add Participant visual
 * language.
 *
 * Currently used by the new full-page Add Group flow. Will also be used by
 * Edit Group after it swaps the existing GroupMetadataFields for this
 * component.
 */
function GroupMetadataSection({
  name,
  description,
  active,
  onNameChange,
  onDescriptionChange,
  onActiveChange,
  sectionNumber = 1,
}: Props) {
  const ts = useContext(TranslationsContext);

  return (
    <SectionCard
      number={sectionNumber}
      title={<T kind="Group Details" />}
      description={
        <T kind="Give the group a name, an optional description, and set whether it's active." />
      }
      intercomTarget="group-details-section"
    >
      <div className="space-y-5">
        <div>
          <label className="block mb-2" style={{ fontWeight: 'var(--font-weight-medium)' }}>
            <T kind="Name" /> <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={translation('e.g. Q1 2026 Cohort')(ts)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            data-intercom-target="group-name-input"
          />
        </div>
        <div>
          <label className="block mb-2" style={{ fontWeight: 'var(--font-weight-medium)' }}>
            <T kind="Description" />
          </label>
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder={translation('What is this group for?')(ts)}
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            data-intercom-target="group-description-input"
          />
        </div>
        <div className="flex items-center justify-between pt-2">
          <div className="flex-1 pr-8">
            <div className="text-sm font-medium mb-1">
              <T kind="Active" />
            </div>
            <p className="text-sm text-muted-foreground">
              <T kind="Inactive groups keep their members and history but can't be assigned new licenses or collections." />
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={active}
            onClick={() => onActiveChange(!active)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${active ? 'bg-primary' : 'bg-muted'}`}
            data-intercom-target="group-active-toggle"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${active ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

export default observer(GroupMetadataSection);
